import { useTranslation } from 'react-i18next';
import { ListTodo, MessageSquare } from 'lucide-react';
import { ScreenLink as Link } from '../permissions/ScreenLink';
import StatusBadge from '../StatusBadge';
import type { TicketLinkRef } from '../../types';

/**
 * P-37 (D-194 Q-179) — the link between a service ticket and the tasks created
 * from it ("יצירת משימה מהקריאה"), shown on BOTH pages: on the ticket, the
 * tasks created from it; on the task, the ticket it came from. Nothing when
 * there is no link.
 */
export default function TicketTaskLinks({
  sourceTicket,
  createdTasks,
  className = '',
}: {
  sourceTicket?: TicketLinkRef | null;
  createdTasks?: TicketLinkRef[] | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const tasks = createdTasks ?? [];
  if (!sourceTicket && tasks.length === 0) return null;
  const num = (n: number | null) => (n != null ? `#${n} ` : '');
  return (
    <div className={`rounded-xl border border-border bg-surface-0 p-4 ${className}`} data-testid="ticket-task-links">
      {sourceTicket && (
        <p className="flex flex-wrap items-center gap-2 text-sm text-text-2">
          <MessageSquare size={16} aria-hidden="true" />
          <span>{t('replyTimer.links.fromTicket')}</span>
          <Link to={`/tickets/${sourceTicket.id}`} className="font-medium text-accent-text hover:underline underline-offset-2">
            <bdi>{num(sourceTicket.ticket_number)}{sourceTicket.title}</bdi>
          </Link>
          <StatusBadge status={sourceTicket.status as never} taskType="ticket" />
        </p>
      )}
      {tasks.length > 0 && (
        <>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-text">
            <ListTodo size={16} aria-hidden="true" />
            {t('replyTimer.links.createdTasks', { n: tasks.length })}
          </h2>
          <ul className="divide-y divide-border">
            {tasks.map((task) => (
              <li key={task.id} className="flex items-center justify-between gap-3 py-2">
                <Link to={`/tasks/${task.id}`} className="min-w-0 truncate text-sm text-accent-text hover:underline underline-offset-2">
                  <bdi>{num(task.ticket_number)}{task.title}</bdi>
                </Link>
                <StatusBadge status={task.status as never} taskType="task" />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
